import { getCustomerIdFromToken } from './getCustomerIdFromToken';

/**
 * The market/state list cached under a flat 'statesData' key is global to
 * the browser, not the logged-in customer. Switching accounts in the same
 * browser (logout doesn't clear it) could hydrate a page with the previous
 * customer's market names and provider/practice counts before - or even if
 * - the live request ever resolves. Scoping the key by customer id makes a
 * read structurally unable to return another customer's cached data.
 */
const CACHE_KEY_PREFIX = 'statesData';

const getScopedKey = (customerId: string): string | null =>
  customerId ? `${CACHE_KEY_PREFIX}:${customerId}` : null;

export const readStatesDataCache = <T>(): T[] | null => {
  const key = getScopedKey(getCustomerIdFromToken());
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as T[]) : null;
  } catch {
    return null;
  }
};

/**
 * requestCustomerId must be the customer who was active when the request
 * that produced `states` was ISSUED, not whoever is active now - an async
 * response resolves after the fact, and if customer A logs out and customer
 * B signs in before A's request settles, writing under "whoever is active
 * now" would store A's data under B's key, defeating the isolation this
 * cache exists for. The write is discarded entirely if the active customer
 * has since changed, rather than falling back to caching it under the
 * wrong (or no) key.
 */
export const writeStatesDataCache = <T>(
  states: T[],
  requestCustomerId: string
): void => {
  if (!requestCustomerId || getCustomerIdFromToken() !== requestCustomerId) {
    return;
  }
  const key = getScopedKey(requestCustomerId);
  if (!key) return;
  try {
    localStorage.setItem(key, JSON.stringify(states));
  } catch {
    // Storage can be full or unavailable (private browsing) - caching is
    // best-effort, the live fetch is still the source of truth.
  }
};
